"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import Slider from "react-slick";
import Link from "next/link";
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { getApiBaseUrl } from "@/lib/apiBase";
import { fetchJsonArray } from "@/lib/fetchJsonSafe";
import EventCoverImage from "@/components/event/EventCoverImage";

const BASE_URL = getApiBaseUrl();

function PrevArrow({ className, style, onClick }) {
  return (
    <button
      type="button"
      aria-label="Өмнөх слайд"
      className={`${className ?? ""} !flex !h-11 !w-11 items-center justify-center !rounded-full border border-slate-200/90 bg-white/95 text-slate-700 shadow-lg backdrop-blur-sm transition before:!hidden hover:border-[#577099] hover:bg-[#577099] hover:text-white z-10`}
      style={style}
      onClick={onClick}
    >
      <ChevronLeft className="h-5 w-5" />
    </button>
  );
}

function NextArrow({ className, style, onClick }) {
  return (
    <button
      type="button"
      aria-label="Дараагийн слайд"
      className={`${className ?? ""} !flex !h-11 !w-11 items-center justify-center !rounded-full border border-slate-200/90 bg-white/95 text-slate-700 shadow-lg backdrop-blur-sm transition before:!hidden hover:border-[#577099] hover:bg-[#577099] hover:text-white z-10`}
      style={style}
      onClick={onClick}
    >
      <ChevronRight className="h-5 w-5" />
    </button>
  );
}

export default function FeaturedEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: events.length > 4,
      speed: 550,
      slidesToShow: Math.min(4, Math.max(1, events.length || 1)),
      slidesToScroll: 1,
      autoplay: events.length > 1,
      autoplaySpeed: 4500,
      arrows: true,
      prevArrow: <PrevArrow />,
      nextArrow: <NextArrow />,
      appendDots: (dots) => (
        <div className="!static mt-8 flex justify-center">
          <ul className="m-0 flex list-none flex-wrap justify-center gap-2 p-0">{dots}</ul>
        </div>
      ),
      customPaging: () => (
        <div className="h-2.5 w-2.5 rounded-full bg-slate-300 transition-all duration-300 hover:bg-[#577099]/60" />
      ),
      responsive: [
        { breakpoint: 1024, settings: { slidesToShow: Math.min(3, Math.max(1, events.length || 1)) } },
        { breakpoint: 640, settings: { slidesToShow: Math.min(2, Math.max(1, events.length || 1)) } },
      ],
    }),
    [events.length]
  );

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const data = await fetchJsonArray(`/api/event?type=event`);
        setEvents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatShortDate = useCallback((d) => {
    try {
      return new Date(d).toLocaleDateString("mn-MN", { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  }, []);

  if (loading) {
    return (
      <section className="relative overflow-hidden border-t border-slate-200/80 bg-gradient-to-b from-slate-50 via-[#f4f6fb] to-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(87,112,153,0.12), transparent 45%), radial-gradient(circle at 80% 60%, rgba(14,165,233,0.08), transparent 40%)",
          }}
        />
        <div className="relative mx-auto mt-8 max-w-[1440px] px-4 py-12">
          <div className="mb-10">
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="mb-2 flex items-center gap-3">
                  <div className="h-1 w-8 animate-pulse rounded-full bg-slate-200" />
                  <div className="h-3 w-36 animate-pulse rounded bg-slate-200" />
                </div>
                <div className="h-8 w-56 animate-pulse rounded-lg bg-slate-200 md:h-9 md:w-64" />
                <div className="mt-2 h-4 w-full max-w-md animate-pulse rounded bg-slate-200" />
              </div>
              <div className="h-12 w-40 animate-pulse rounded-full bg-slate-200" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-[320px] animate-pulse rounded-2xl bg-slate-200/80 md:h-[380px]" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden border-t border-slate-200/80 bg-gradient-to-b from-slate-50 via-[#f4f6fb] to-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 10%, rgba(87,112,153,0.14), transparent 42%), radial-gradient(circle at 85% 70%, rgba(56,189,248,0.1), transparent 38%)",
        }}
      />
      <div className="relative mx-auto mt-8 max-w-[1440px] px-4 py-12">
        <div className="mb-10">
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <div className="h-1 w-8 rounded-full bg-[#577099]" />
                <span className="text-xs font-medium uppercase tracking-wide text-blue-600">
                  Онцлох арга хэмжээ
                </span>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 md:text-3xl">Арга хэмжээнүүд</h2>
              <p className="mt-1 text-sm text-slate-600">
                Ойрын үйл ажиллагаа, уулзалт, сургалт — нэг дороос хараад бүртгүүлээрэй.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                href="/event/events"
                className="flex items-center gap-2 rounded-full bg-[#577099] px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:bg-[#4a5f85] hover:shadow-xl"
              >
                <span>Бүгдийг харах</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {events.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300/80 bg-white/70 px-8 py-20 text-center shadow-inner">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#577099]/10 text-[#577099]">
              <CalendarDays className="h-8 w-8" />
            </div>
            <p className="text-lg font-semibold text-slate-800">Одоогоор арга хэмжээ байхгүй байна</p>
            <p className="mt-2 text-sm text-slate-500">Удахгүй шинэ үйл ажиллагаанууд нэмэгдэнэ.</p>
            <Link
              href="/event/events"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#577099] hover:text-[#3d5280] hover:underline"
            >
              Арга хэмжээний хуудас руу очих
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="relative w-full">
            <Slider {...sliderSettings}>
              {events.map((event) => {
                const hasEnded = event.finish_date ? new Date(event.finish_date) < new Date() : false;
                return (
                  <div key={event.id} className="px-2 pb-1">
                    <Link href={`/event/${event.id}`} className="block outline-none focus-visible:ring-2 focus-visible:ring-[#577099] focus-visible:ring-offset-2 rounded-2xl">
                      <article
                        className={`group relative h-[300px] w-full cursor-pointer overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/60 transition duration-500 md:h-[380px] ${
                          hasEnded
                            ? "opacity-90 saturate-75"
                            : "hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#577099]/15 hover:ring-[#577099]/25"
                        }`}
                      >
                        <EventCoverImage
                          eventId={event.id}
                          rawImage={event.image}
                          baseUrl={BASE_URL}
                          alt={event.event_name || "Арга хэмжээ"}
                          className={`object-cover transition duration-700 ease-out ${
                            hasEnded ? "" : "group-hover:scale-105"
                          }`}
                        />

                        <div
                          className={`absolute inset-0 ${
                            hasEnded
                              ? "bg-gradient-to-t from-slate-900/85 via-slate-800/45 to-slate-900/20"
                              : "bg-gradient-to-t from-[#0f172a]/92 via-[#0f172a]/35 to-transparent"
                          }`}
                        />

                        {hasEnded && (
                          <div className="absolute right-3 top-3 z-20 rounded-full bg-slate-600/95 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-md backdrop-blur-sm sm:text-xs">
                            Дууссан
                          </div>
                        )}

                        <div
                          className={`absolute left-3 top-3 z-20 flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold text-white shadow-md backdrop-blur-sm sm:left-4 sm:top-4 ${
                            hasEnded
                              ? "bg-slate-600/90"
                              : "bg-gradient-to-r from-sky-500 to-[#577099]"
                          }`}
                        >
                          <CalendarDays className="h-3.5 w-3.5 opacity-90" />
                          {formatShortDate(event.start_date)}
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 z-10 p-5 text-white md:p-6">
                          <div className="mb-2 flex flex-wrap items-start gap-2">
                            <h3
                              className={`text-lg font-bold leading-snug drop-shadow-md md:text-xl ${
                                hasEnded ? "text-white/90" : ""
                              }`}
                            >
                              {event.event_name}
                            </h3>
                          </div>
                          {event.event_location_text && (
                            <p className="mb-2 flex items-start gap-2 text-sm text-white/90">
                              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sky-200" />
                              <span className="line-clamp-1">{event.event_location_text}</span>
                            </p>
                          )}
                          {event.event_description && (
                            <p className="line-clamp-2 text-sm leading-relaxed text-white/75">
                              {event.event_description.length > 120
                                ? `${event.event_description.slice(0, 120)}…`
                                : event.event_description}
                            </p>
                          )}
                          {hasEnded && event.finish_date && (
                            <p className="mt-3 text-xs font-medium text-white/55">
                              Дууссан:{" "}
                              {new Date(event.finish_date).toLocaleDateString("mn-MN", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </p>
                          )}
                        </div>
                      </article>
                    </Link>
                  </div>
                );
              })}
            </Slider>
          </div>
        )}
      </div>
    </section>
  );
}
